import React, { useCallback, useRef } from 'react';
import { identity } from 'ramda';
import uuid from 'uuid-random';
import { useTranslations } from 'js/hooks/i18n';
import { I18n } from 'js/components/common/I18n';
import { getClassNames } from 'js/utils/classNames.util';
import buildClassName from 'js/utils/classNameBuilder.util';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import style from './RadioShallow.scss';

export const RadioShallow = ({
    id,
    hint = '',
    label,
    children = undefined,
    onChange,
    onKeyDown = undefined,
    classNames,
    className = '',
    reverse = false,
    stretch = false,
    wrapText = false,
    disabled = false,
    readOnly = false,
    ...props
}) => {
    const idRef = useRef(id || uuid());
    const radioRef = useRef();
    const translate = useTranslations();
    const handleOnKeyDown = useCallback((event) => {
        const { key } = event;

        onKeyDown?.();

        if (key === ' ' || key === 'Spacebar') {
            event.preventDefault();

            radioRef.current?.click();
        }
    }, [radioRef, onKeyDown]);
    const radioClassName = getClassNames([
        className,
        buildClassName(['radio'], style, classNames),
        reverse && style.reverse,
        stretch && style.stretch,
        (disabled || readOnly) && style.disabledRadio
    ]);
    const labelClassName = getClassNames([
        buildClassName(['label'], style, classNames),
        wrapText && style.wrapText,
        (disabled || readOnly) && style.disabledLabel
    ]);

    const labelContent = typeof label === 'string'
        ? <I18n tKey={label} defaultValue={label} />
        : label;

    const hintClassname = buildClassName(['hint'], style, classNames);
    const hintContent = typeof hint === 'string'
        ? <I18n tKey={hint} defaultValue={hint} className={hintClassname} />
        : <span className={hintClassname}>{hint}</span>;

    return (
        <AccessibilityElement
            tagName="label"
            htmlFor={idRef.current}
            className={radioClassName}
            onKeyDown={handleOnKeyDown}
            tabIndex={disabled || readOnly ? -1 : 0}
            aria-label={props['aria-label'] || translate('playtech.mobile.system.radio.aria-label')}
        >
            <input
                type="radio"
                tabIndex={-1}
                ref={radioRef}
                id={idRef.current}
                disabled={disabled}
                readOnly={readOnly}
                onChange={readOnly ? identity : onChange}
                className={`${buildClassName(['input'], style, classNames)} ${readOnly ? buildClassName(['readOnly'], style, classNames) : ''}`}
                {...props}
            />
            <span className={buildClassName(['radioMark'], style, classNames)} />
            {children}
            <span className={labelClassName}>
                {labelContent}
                {hint && hintContent}
            </span>
        </AccessibilityElement>
    );
};
